import React from 'react';
import * as Cesium from 'cesium';
import cesiumPackage from 'cesium/package.json';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import Button from '@material-ui/core/Button';
import MaterialIcon from './../../../components/MaterialIcon';
import { notification } from 'antd';

import Config from '../../../constants/appConfig';

import emb200Model from './../../../assets/replay_3d/models/emb-200/aeronave.glb';
import airTractorAt502bModel from './../../../assets/replay_3d/models/air-tractor-at-502b/aeronave.glb';
import airTractorAt802Model from './../../../assets/replay_3d/models/air-tractor-at-802/aeronave.glb';
import cessna188Model from './../../../assets/replay_3d/models/cessna-188/aeronave.glb';
import './map3d.css';

const cesiumIonToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJmMjFlMzc4ZC1hMjkzLTRhYTQtYTcxZi01Y2EzMTM0NTczYmUiLCJpZCI6MzIwMzMwLCJpYXQiOjE3NTIxNTc3OTV9.3Hevk1CLhRrpuR2DSjyD2pGdzwmmsKMn0PApg0FoxNc';
const cesiumBaseUrl = 'https://cdn.jsdelivr.net/npm/cesium@' + cesiumPackage.version + '/Build/Cesium/';
const loadingTimeoutMs = 20000;

const aircraftModels = [
  {
    id: 'emb200',
    label: 'EMB-200 Ipanema',
    url: emb200Model,
    scale: 0.2,
    modelHeadingOffsetDeg: 0,
    modelPitchOffsetDeg: 90,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 5,
    sprayMistVerticalOffsetMeters: 0.3
  },
  {
    id: 'air-tractor-at-502b',
    label: 'Air Tractor AT-502B',
    url: airTractorAt502bModel,
    scale: 0.05,
    modelHeadingOffsetDeg: 0,
    modelPitchOffsetDeg: -8,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 13,
    sprayMistVerticalOffsetMeters: -8.5,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: ['objpropeller_basic_airtractor'],
        axis: 'z',
        rpm: 900,
        spinSign: -1,
        pivot: [-0.5831, 87.3068, 531.6978]
      },
      {
        id: 'bomba_eolica',
        nodes: ['objpropeller_small_airtractor'],
        axis: 'z',
        rpm: 1400,
        spinSign: -1,
        pivot: [-0.1409, -59.7121, 262.3489]
      }
    ]
  },
  {
    id: 'air-tractor-at-802',
    label: 'Air Tractor AT-802',
    url: airTractorAt802Model,
    scale: 0.125,
    modelHeadingOffsetDeg: 0,
    modelPitchOffsetDeg: -8,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 13,
    sprayMistVerticalOffsetMeters: -8.5,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: ['objpropeller_basic'],
        axis: 'z',
        rpm: 900,
        spinSign: -1,
        pivot: [-0.25, 40.8, 249.05]
      },
      {
        id: 'bomba_eolica',
        nodes: ['objpropeller_small'],
        axis: 'z',
        rpm: 1400,
        spinSign: -1,
        pivot: [-0.06608963012694957, -28.006233215331928, 123.04706573486402]
      }
    ]
  },
  {
    id: 'cessna-188',
    label: 'Cessna 188 AG',
    url: cessna188Model,
    scale: 1.5,
    modelHeadingOffsetDeg: 180,
    modelPitchOffsetDeg: -8,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 5,
    sprayMistVerticalOffsetMeters: 0.3,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: [
          'cessna_188_ag_ipanema_propeller_Cylinder32_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder104_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder108_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder01',
          'cessna_188_ag_ipanema_propeller_Cylinder31',
          'cessna_188_ag_ipanema_propeller_Cylinder301',
          'cessna_188_ag_ipanema_propeller_Cylinder102_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder106_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder110_pivot'
        ],
        axis: 'z',
        rpm: 900,
        spinSign: -1,
        pivot: [0.002090245485305786, 7.712601900100708, -17.66800308227539]
      }
    ]
  }
];

const toNumber = (value, defaultValue) => {
  const numberValue = Number(value);
  return isNaN(numberValue) ? defaultValue : numberValue;
};

class Map3D extends React.Component {
  state = {
    fullscreen: false,
    panelCollapsed: false,
    loading: true,
    jobs: [],
    planes: [],
    message: '',
    currentJobIndex: 0,
    currentPlaneIndex: 0,
    freeMode: false,
    focusItems: [],
    planeFocusItems: []
  };

  mounted = false;
  loaded = false;
  refreshTimer = null;
  loadingTimer = null;
  loadingTilesTimer = null;
  loadingStartedAt = 0;
  initialDataReady = false;
  initialTilesReady = false;
  initialLoadComplete = false;
  tileLoadProgressListener = null;
  viewer = null;
  focusItems = [];
  aircraftEntities = [];
  aircraftScalePreRenderListener = null;
  aircraftScaleReferenceDistanceMeters = 1300;
  aircraftScaleMinFactor = 0.45;
  aircraftScaleMaxFactor = 2.4;
  containerId = 'onlineFarmCesiumContainer_' + Date.now();

  componentDidMount() {
    this.mounted = true;
    this.configureCesiumRuntime();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    this.startInitialLoading();
    setTimeout(this.boot, 0);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.jobs !== this.props.jobs || prevProps.planes !== this.props.planes) {
      this.loadMapData();
    }

    if (prevState.panelCollapsed !== this.state.panelCollapsed && this.viewer && !this.viewer.isDestroyed()) {
      setTimeout(() => {
        if (this.viewer && !this.viewer.isDestroyed()) {
          this.viewer.resize();
          this.viewer.scene.requestRender();
        }
      }, 240);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (this.loadingTimer) clearTimeout(this.loadingTimer);
    if (this.loadingTilesTimer) clearTimeout(this.loadingTilesTimer);
    this.removeInitialTilesListener();
    this.removeAircraftScaleListener();
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    if (this.viewer && !this.viewer.isDestroyed()) this.viewer.destroy();
    this.viewer = null;
  }

  configureCesiumRuntime = () => {
    window.CESIUM_BASE_URL = cesiumBaseUrl;
    window.Cesium = Cesium;
    Cesium.Ion.defaultAccessToken = cesiumIonToken;
  };

  boot = () => {
    if (!this.mounted || this.viewer) return;

    const target = document.getElementById(this.containerId);
    if (!target) return;

    this.viewer = new Cesium.Viewer(this.containerId, {
      shouldAnimate: false,
      animation: false,
      timeline: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      baseLayerPicker: false,
      infoBox: false,
      selectionIndicator: false,
      shadows: false,
      terrainShadows: Cesium.ShadowMode.DISABLED,
      fullscreenButton: false,
      requestRenderMode: false
    });

    this.viewer.scene.rethrowRenderErrors = false;
    this.viewer.useBrowserRecommendedResolution = true;
    this.viewer.targetFrameRate = 60;

    const controller = this.viewer.scene && this.viewer.scene.screenSpaceCameraController;
    if (controller) {
      controller.enableRotate = true;
      controller.enableZoom = true;
      controller.enableTranslate = true;
      controller.enableTilt = true;
      controller.enableLook = true;
    }

    this.applyBasemap();
    this.installInitialTilesListener();
    this.loadMapData();
  };

  applyBasemap = () => {
    if (!this.viewer) return;

    if (Cesium.ArcGisMapServerImageryProvider && Cesium.ArcGisMapServerImageryProvider.fromUrl) {
      Cesium.ArcGisMapServerImageryProvider.fromUrl('https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer')
        .then((provider) => {
          if (!this.viewer || this.viewer.isDestroyed()) return;
          this.viewer.imageryLayers.removeAll();
          this.viewer.imageryLayers.addImageryProvider(provider);
          this.viewer.scene.requestRender();
        })
        .catch(() => {});
    }
  };

  startInitialLoading = () => {
    if (!this.mounted) return;

    if (this.loadingTimer) clearTimeout(this.loadingTimer);
    if (this.loadingTilesTimer) clearTimeout(this.loadingTilesTimer);

    this.loadingStartedAt = Date.now();
    this.initialDataReady = false;
    this.initialTilesReady = false;
    this.initialLoadComplete = false;

    if (!this.state.loading) {
      this.setState({ loading: true });
    }

    this.loadingTimer = setTimeout(() => {
      this.finishInitialLoading();
    }, loadingTimeoutMs);
  };

  installInitialTilesListener = () => {
    if (!this.viewer || !this.viewer.scene || !this.viewer.scene.globe || !this.viewer.scene.globe.tileLoadProgressEvent) return;
    if (this.tileLoadProgressListener) return;

    this.tileLoadProgressListener = (remainingTiles) => {
      if (remainingTiles === 0) {
        this.initialTilesReady = true;
        this.tryHideInitialLoading();
      }
    };

    this.viewer.scene.globe.tileLoadProgressEvent.addEventListener(this.tileLoadProgressListener);
  };

  removeInitialTilesListener = () => {
    if (this.viewer && this.viewer.scene && this.viewer.scene.globe && this.viewer.scene.globe.tileLoadProgressEvent && this.tileLoadProgressListener) {
      this.viewer.scene.globe.tileLoadProgressEvent.removeEventListener(this.tileLoadProgressListener);
    }
    this.tileLoadProgressListener = null;
  };

  markInitialDataReady = () => {
    this.initialDataReady = true;

    if (this.viewer && this.viewer.scene && this.viewer.scene.globe && this.viewer.scene.globe.tilesLoaded) {
      this.initialTilesReady = true;
    }

    if (this.loadingTilesTimer) clearTimeout(this.loadingTilesTimer);
    this.loadingTilesTimer = setTimeout(() => {
      if (this.viewer && this.viewer.scene && this.viewer.scene.globe && this.viewer.scene.globe.tilesLoaded) {
        this.initialTilesReady = true;
      }
      this.tryHideInitialLoading();
    }, 900);

    this.tryHideInitialLoading();
  };

  tryHideInitialLoading = () => {
    if (!this.mounted || this.initialLoadComplete || !this.initialDataReady) return;

    const elapsed = Date.now() - this.loadingStartedAt;
    if (this.initialTilesReady || elapsed >= loadingTimeoutMs) {
      this.finishInitialLoading();
    }
  };

  finishInitialLoading = () => {
    if (!this.mounted || this.initialLoadComplete) return;

    this.initialLoadComplete = true;

    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
    if (this.loadingTilesTimer) {
      clearTimeout(this.loadingTilesTimer);
      this.loadingTilesTimer = null;
    }

    this.removeInitialTilesListener();

    if (this.state.loading) {
      this.setState({ loading: false });
    }
  };

  hideLoading = () => {
    this.finishInitialLoading();
  };

  handleFullscreenChange = () => {
    this.setState({ fullscreen: !!document.fullscreenElement });
  };

  toggleFullscreen = () => {
    const element = document.querySelector('.replay3d-base-overlay');
    if (!element) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
      this.setState({ fullscreen: false });
      return;
    }

    if (element.requestFullscreen) {
      element.requestFullscreen();
      this.setState({ fullscreen: true });
    }
  };

  togglePanelVisibility = () => {
    this.setState({ panelCollapsed: !this.state.panelCollapsed });
  };

  loadMapData = () => {
    if (!this.viewer) return;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const jobs = this.getRunningJobs();
    const planes = this.getPlanesFromJobs(jobs);
    const hasData = jobs.length > 0 || planes.length > 0;
    const message = jobs.length > 0 ? '' : this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;

    if (!hasData) {
      this.viewer.entities.removeAll();
      this.setState({ jobs: [], planes: [], focusItems: [], planeFocusItems: [], message: message });
      this.flyToDefaultPlace();
      this.markInitialDataReady();
      return;
    }

    this.setState(this.initialLoadComplete ? { message: message } : { loading: true, message: message });

    Promise.all([
      this.loadJobAreas(jobs),
      this.loadPlanePositions(planes)
    ]).then((result) => {
      if (!this.mounted || !this.viewer || this.viewer.isDestroyed()) return;

      const areasByJob = result[0];
      const positionsByPlane = result[1];
      const mapResult = this.drawOnlineMap(jobs, planes, areasByJob, positionsByPlane);
      const focusItems = mapResult.focusItems;
      const planeFocusItems = mapResult.planeFocusItems;
      const safeIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
      const safePlaneIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, planeFocusItems.length);
      this.focusItems = focusItems;
      this.setState({
        jobs: jobs,
        planes: planes,
        focusItems: focusItems,
        planeFocusItems: planeFocusItems,
        currentJobIndex: safeIndex,
        currentPlaneIndex: safePlaneIndex,
        message: message
      }, () => {
        this.markInitialDataReady();
        this.loadApplicationReportsForVisibleJobs(jobs);
      });

      this.refreshTimer = setTimeout(this.loadMapData, 60000);
    }).catch((error) => {
      if (!this.mounted) return;
      this.setState({ message: this.props.res.NAO_FOI_POSSIVEL_CARREGAR_MAPA_FAZENDA_ONLINE }, () => {
        this.markInitialDataReady();
      });
      notification.open({
        message: this.props.res.ERRO,
        description: this.props.res.ERRO_MSG,
        icon: <MaterialIcon icon="error" className="text-danger" />
      });
    });
  };

  getRunningJobs = () => {
    const jobs = Array.isArray(this.props.jobs) ? this.props.jobs : [];
    const dateIni = this.props.dateIni ? this.props.dateIni.getTime() : 0;
    const dateEnd = this.props.dateEnd ? this.props.dateEnd.getTime() : 0;

    return jobs.filter((job) => this.isOnlineFarmJobInPeriod(job, dateIni, dateEnd));
  };

  isOnlineFarmJobInPeriod = (job, dateIni, dateEnd) => {
    if (!job || job.status === 'canceled' || job.status === 'deleted') return false;

    const dates = this.getOnlineFarmJobDates(job);
    if (!dates.length) return true;

    for (let i = 0; i < dates.length; i++) {
      if (dates[i] >= dateIni && dates[i] < dateEnd) return true;
    }

    return false;
  };

  getOnlineFarmJobDates = (job) => {
    const dates = [];
    const addDate = (value) => {
      const numberValue = Number(value || 0);
      if (numberValue > 0) {
        dates.push(numberValue);
        return;
      }

      const parsed = value ? new Date(value).getTime() : 0;
      if (parsed > 0) dates.push(parsed);
    };

    const addItemDates = (item) => {
      if (!item) return;
      addDate(item.startDate);
      addDate(item.endDate);
      addDate(item.date);
      addDate(item.datetime);
      addDate(item.created);
      addDate(item.updated);
    };

    addItemDates(job);

    const lists = ['list_exe', 'list_plan', 'list_analise'];
    for (let i = 0; i < lists.length; i++) {
      const arr = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < arr.length; j++) addItemDates(arr[j]);
    }

    return dates;
  };

  getPlaneId = (plane) => {
    if (!plane) return 0;
    const candidates = [plane.id, plane.id_plane, plane.idPlane, plane.id_aircraft, plane.id_aeronave];

    for (let i = 0; i < candidates.length; i++) {
      const id = toNumber(candidates[i], 0);
      if (id > 0) return id;
    }

    return 0;
  };

  getPlaneCatalog = () => {
    const currentPlanes = Array.isArray(this.props.planes) ? this.props.planes : [];
    const editPlanes = Array.isArray(this.props.planeCatalog) ? this.props.planeCatalog : [];
    return currentPlanes.concat(editPlanes);
  };

  findPlaneById = (idPlane) => {
    const id = toNumber(idPlane, 0);
    if (id <= 0) return null;

    const catalog = this.getPlaneCatalog();
    let fallback = null;

    for (let i = 0; i < catalog.length; i++) {
      const item = catalog[i];
      if (!item || this.getPlaneId(item) !== id) continue;

      if (!fallback) fallback = item;

      if (this.hasAircraftModelInfo(item)) {
        return item;
      }
    }

    return fallback;
  };

  hasAircraftModelInfo = (plane) => {
    if (!plane) return false;
    const model3D = this.normalizeReplay3DModelId(plane.model3D);
    if (model3D) return true;

    const text = this.buildAircraftSearchText(plane);
    return !!text && (
      text.indexOf('airtractor') >= 0 ||
      text.indexOf('at502') >= 0 ||
      text.indexOf('at802') >= 0 ||
      text.indexOf('cessna') >= 0 ||
      text.indexOf('embraer') >= 0 ||
      text.indexOf('ipanema') >= 0 ||
      text.indexOf('emb200') >= 0
    );
  };

  getPlanesFromJobs = (jobs) => {
    const ret = [];
    const added = {};

    const addPlane = (plane, override) => {
      const idPlane = this.getPlaneId(Object.assign({}, plane || {}, override || {}));
      if (idPlane <= 0 || added[idPlane]) return;

      const catalogPlane = this.findPlaneById(idPlane) || {};
      const merged = Object.assign({}, plane || {}, catalogPlane, override || {}, {
        id: idPlane,
        name: (override && override.name) || catalogPlane.name || (plane && (plane.name || plane.plane_name || plane.plane)) || (this.props.res.AERONAVE + ' ' + idPlane),
        plane_name: catalogPlane.name || (plane && (plane.plane_name || plane.plane || plane.name)) || ''
      });

      added[idPlane] = true;
      ret.push(merged);
    };

    const propPlanes = Array.isArray(this.props.planes) ? this.props.planes : [];
    for (let i = 0; i < propPlanes.length; i++) addPlane(propPlanes[i]);

    for (let i = 0; i < jobs.length; i++) {
      const lists = ['list_exe', 'list_plan', 'list_analise'];
      for (let l = 0; l < lists.length; l++) {
        const arr = Array.isArray(jobs[i][lists[l]]) ? jobs[i][lists[l]] : [];
        for (let j = 0; j < arr.length; j++) {
          const idPlane = toNumber(arr[j].id_plane || arr[j].idPlane || arr[j].plane_id, 0);
          if (idPlane > 0) {
            addPlane(arr[j], {
              id: idPlane,
              name: arr[j].plane_name || arr[j].plane || arr[j].name || (this.props.res.AERONAVE + ' ' + idPlane),
              plane_name: arr[j].plane_name || arr[j].plane || arr[j].name || ''
            });
          }
        }
      }
    }

    return ret;
  };

  loadJobAreas = (jobs) => {
    const promises = jobs.map((job) => this.fetchAreasForJob(job));
    return Promise.all(promises).then((result) => {
      const map = {};
      for (let i = 0; i < result.length; i++) map[jobs[i].id] = result[i];
      return map;
    });
  };

  fetchAreasForJob = (job) => {
    return Promise.all([
      this.fetchJobArea(job.id, 'execucao'),
      this.fetchJobArea(job.id, 'planejamento')
    ]).then((result) => {
      const executionAreas = Array.isArray(result[0]) ? result[0] : [];
      const planningAreas = Array.isArray(result[1]) ? result[1] : [];
      const areas = planningAreas.length ? planningAreas.slice() : executionAreas.slice();

      areas.executionAreas = executionAreas;
      areas.planningAreas = planningAreas;
      areas.applicationReport = null;

      return areas;
    });
  };

  loadApplicationReportsForVisibleJobs = (jobs) => {
    if (!this.viewer || this.viewer.isDestroyed() || !Array.isArray(jobs) || !jobs.length) return;

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];

      if (!this.hasJobExecution(job)) {
        continue;
      }

      this.fetchJobApplicationReport(job).then((report) => {
        if (!this.mounted || !this.viewer || this.viewer.isDestroyed() || !report) return;

        this.drawJobApplicationReport(job, report);
        this.viewer.scene.requestRender();
      });
    }
  };

  hasJobExecution = (job) => {
    if (!job) return false;

    if (Array.isArray(job.list_exe) && job.list_exe.length > 0) return true;
    if (toNumber(job.id_execution, 0) > 0) return true;
    if (toNumber(job.id_execucao, 0) > 0) return true;
    if (toNumber(job.id_exe, 0) > 0) return true;

    const type = String(job.type || job.tipo || '').toLowerCase();
    if (type === 'execucao' || type === 'execução') return true;

    return false;
  };

  fetchJobApplicationReport = (job) => {
    const taskIds = this.getJobResultTaskIds(job);

    if (!taskIds.length) {
      return Promise.resolve(null);
    }

    const tryTask = (index) => {
      if (index >= taskIds.length) return Promise.resolve(null);

      return this.fetchJobResult(taskIds[index]).then((result) => {
        if (this.hasJobApplicationReport(result)) {
          return result;
        }

        return tryTask(index + 1);
      });
    };

    return tryTask(0);
  };

  hasJobApplicationReport = (result) => {
    if (!result) return false;

    if (result.apl && Array.isArray(result.apl.aplicacao) && result.apl.aplicacao.length) return true;
    if (Array.isArray(result.aplicacao) && result.aplicacao.length) return true;
    if (Array.isArray(result.application) && result.application.length) return true;
    if (Array.isArray(result.app) && result.app.length) return true;

    return false;
  };

  fetchJobResult = (taskId) => {
    const apiUrl = Config.server + '/getResult/' + taskId;
    const options = {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Authorization': localStorage.getItem('access-token-jwt'),
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    };

    return fetch(apiUrl, options).then((res) => {
      if (res.status !== 200) return null;
      return res.text();
    }).then((result) => {
      if (!result || result === 'file not found.') return null;

      try {
        const parsed = JSON.parse(result);
        return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      } catch (e) {
        return null;
      }
    }).catch(() => null);
  };

  getJobResultTaskIds = (job) => {
    const ret = [];
    const added = {};

    const addId = (value) => {
      const id = toNumber(value, 0);
      if (id > 0 && !added[id]) {
        added[id] = true;
        ret.push(id);
      }
    };

    addId(job && job.id_task);
    addId(job && job.idTask);
    addId(job && job.task_id);
    addId(job && job.task);
    addId(job && job.id_task_result);
    addId(job && job.id_result);
    addId(job && job.id_job_task);
    addId(job && job.id_task_output);
    addId(job && job.result_task_id);
    addId(job && job.taskResultId);

    const lists = ['list_exe', 'list_analise', 'list_plan'];
    for (let i = 0; i < lists.length; i++) {
      const list = job && Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < list.length; j++) {
        const item = list[j] || {};
        addId(item.id_task);
        addId(item.idTask);
        addId(item.task_id);
        addId(item.task);
        addId(item.id_task_result);
        addId(item.id_result);
        addId(item.id_job_task);
        addId(item.id_task_output);
        addId(item.result_task_id);
        addId(item.taskResultId);
      }
    }

    return ret;
  };

  fetchJobArea = (idJob, type) => {
    const apiUrl = Config.server + '/job_area/' + idJob + '/' + type;
    const options = { method: 'GET', headers: { 'Authorization': localStorage.getItem('access-token-jwt') } };

    return fetch(apiUrl, options).then((res) => {
      if (res.status !== 200) return [];
      return res.json();
    }).then((result) => Array.isArray(result) ? result : []).catch(() => []);
  };

  loadPlanePositions = (planes) => {
    const promises = planes.map((plane) => this.fetchPlanePosition(plane));
    return Promise.all(promises).then((result) => {
      const map = {};
      for (let i = 0; i < result.length; i++) {
        if (result[i]) map[this.getPlaneId(planes[i])] = result[i];
      }
      return map;
    });
  };

  getPlanePositionRequestId = (plane) => {
    if (!plane) return 0;

    const idDgps = toNumber(plane.id_dgps, 0);
    if (idDgps > 0) return idDgps;

    const idDgpsPlane = toNumber(plane.idDgps, 0);
    if (idDgpsPlane > 0) return idDgpsPlane;

    const idDgpsCamel = toNumber(plane.idDGPS, 0);
    if (idDgpsCamel > 0) return idDgpsCamel;

    return 0;
  };

  fetchPlanePosition = (plane) => {
    if (!plane) return Promise.resolve(null);

    const requestId = this.getPlanePositionRequestId(plane);
    if (requestId <= 0) return Promise.resolve(this.getValidPosition(plane) ? plane : null);

    const apiUrl = Config.server + '/plane_position/' + requestId;
    const options = { method: 'GET', headers: { 'Authorization': localStorage.getItem('access-token-jwt') } };

    return fetch(apiUrl, options).then((res) => {
      if (res.status !== 200) return null;
      return res.json();
    }).then((result) => Array.isArray(result) ? (result.length ? result[0] : null) : result).catch(() => null);
  };

  drawOnlineMap = (jobs, planes, areasByJob, positionsByPlane) => {
    if (!this.viewer || this.viewer.isDestroyed()) return { focusItems: [], planeFocusItems: [] };
    this.viewer.entities.removeAll();
    this.aircraftEntities = [];
    this.applicationGeometryKeys = {};

    let bounds = [];
    const focusItems = [];
    const planeFocusItems = [];
    const planeById = this.buildPlaneById(planes);
    const planesByJob = {};
    const planeJobsById = {};

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      const areas = areasByJob[job.id] || [];
      const areaPoints = this.drawJobAreas(job, areas);
      bounds = bounds.concat(areaPoints);

      const servicePoint = this.getJobServicePosition(job, areas);
      if (!servicePoint) continue;

      const jobPlanes = this.getJobPlanes(job, planeById);
      planesByJob[job.id] = jobPlanes;

      for (let j = 0; j < jobPlanes.length; j++) {
        const idPlane = toNumber(jobPlanes[j].id, 0);
        if (idPlane <= 0) continue;
        if (!planeJobsById[idPlane]) planeJobsById[idPlane] = [];
        planeJobsById[idPlane].push(job);
      }

      const planeNames = jobPlanes.map((plane) => this.getPlaneDisplayName(plane)).filter(Boolean);
      const focusLabel = this.getJobFocusLabel(job, planeNames);
      const focusBounds = areaPoints.length ? areaPoints.concat([servicePoint]) : [servicePoint];

      focusItems.push({
        type: 'job',
        job: job,
        point: servicePoint,
        bounds: focusBounds,
        label: focusLabel,
        planeNames: planeNames
      });
    }

    const drawnPlanes = {};
    for (let i = 0; i < planes.length; i++) {
      const plane = planeById[this.getPlaneId(planes[i])] || planes[i];
      const idPlane = this.getPlaneId(plane);
      if (idPlane <= 0 || drawnPlanes[idPlane]) continue;

      const rawPosition = positionsByPlane && positionsByPlane[idPlane] ? positionsByPlane[idPlane] : plane;
      const currentPoint = this.getValidPosition(rawPosition) || this.getPlaneFallbackPosition(plane, jobs, areasByJob);
      if (!currentPoint) continue;

      drawnPlanes[idPlane] = true;
      bounds.push(currentPoint);
      this.drawPlane(plane, currentPoint, rawPosition, null);

      const planeJobs = planeJobsById[idPlane] || [];
      let planeBounds = [currentPoint];
      for (let j = 0; j < planeJobs.length; j++) {
        const areas = areasByJob[planeJobs[j].id] || [];
        for (let k = 0; k < areas.length; k++) {
          planeBounds = planeBounds.concat(this.parseAreaPoints(areas[k]));
        }
      }

      planeFocusItems.push({
        type: 'plane',
        plane: plane,
        point: currentPoint,
        bounds: planeBounds,
        label: this.getPlaneDisplayName(plane),
        jobs: planeJobs
      });
    }

    const safeIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    const safePlaneIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, planeFocusItems.length);
    if (!this.state.freeMode && focusItems[safeIndex]) {
      this.flyToJobFocus(focusItems[safeIndex]);
    } else if (!this.state.freeMode && planeFocusItems[safePlaneIndex]) {
      this.flyToJobFocus(planeFocusItems[safePlaneIndex]);
    } else {
      this.flyToPoints(bounds);
    }

    this.viewer.scene.requestRender();
    return { focusItems: focusItems, planeFocusItems: planeFocusItems };
  };

  buildPlaneById = (planes) => {
    const map = {};
    const catalog = this.getPlaneCatalog();
    const allPlanes = (Array.isArray(planes) ? planes : []).concat(catalog);

    for (let i = 0; i < allPlanes.length; i++) {
      const item = allPlanes[i];
      const id = this.getPlaneId(item);
      if (id <= 0) continue;

      if (!map[id]) {
        map[id] = item;
      } else {
        map[id] = Object.assign({}, map[id], item);
      }
    }

    return map;
  };

  getJobPlanes = (job, planeById) => {
    const ret = [];
    const added = {};
    const lists = ['list_exe', 'list_plan', 'list_analise'];

    for (let l = 0; l < lists.length; l++) {
      const arr = Array.isArray(job[lists[l]]) ? job[lists[l]] : [];
      for (let i = 0; i < arr.length; i++) {
        const item = arr[i] || {};
        const idPlane = toNumber(item.id_plane || item.idPlane || item.plane_id, 0);
        if (idPlane <= 0 || added[idPlane]) continue;

        const planeFromList = planeById[idPlane] || this.findPlaneById(idPlane) || {};
        const merged = Object.assign({}, item, planeFromList, {
          id: idPlane,
          name: planeFromList.name || item.plane_name || item.plane || (this.props.res.AERONAVE + ' ' + idPlane),
          plane_name: planeFromList.name || item.plane_name || item.plane || '',
          job_plane_name: item.plane_name || item.plane || ''
        });

        added[idPlane] = true;
        ret.push(merged);
      }
    }

    return ret;
  };

  getPlaneDisplayName = (plane) => {
    if (!plane) return '';
    return plane.name || plane.plane_name || plane.plane || plane.prefix || (this.getPlaneId(plane) ? (this.props.res.AERONAVE + ' ' + this.getPlaneId(plane)) : '');
  };

  getJobDisplayName = (job) => {
    if (!job) return '';
    return job.title || job.name || (this.props.res.TRABALHO + ' ' + job.id);
  };

  getJobFocusLabel = (job, planeNames) => {
    const jobName = this.getJobDisplayName(job);
    const planeText = planeNames && planeNames.length ? planeNames.join(', ') : this.props.res.AERONAVE_NAO_INFORMADA;
    return jobName + ' - ' + planeText;
  };

  getSafeJobIndex = (index, total) => {
    if (!total || total <= 0) return 0;
    const parsed = toNumber(index, 0);
    if (parsed < 0) return 0;
    if (parsed >= total) return total - 1;
    return parsed;
  };

  getJobServicePosition = (job, areas) => {
    let points = [];
    for (let i = 0; i < areas.length; i++) {
      points = points.concat(this.parseAreaPoints(areas[i]));
    }

    const center = this.getCenterFromPoints(points);
    if (center) return center;

    return this.getJobFallbackPosition(job);
  };

  getJobFallbackPosition = (job) => {
    const candidates = [];
    const addCandidates = (item) => {
      if (!item) return;
      candidates.push({ lat: item.center_lat, lng: item.center_lng });
      candidates.push({ lat: item.hangar_lat, lng: item.hangar_lng });
      candidates.push({ lat: item.lat, lng: item.lng });
      candidates.push({ lat: item.latitude, lng: item.longitude });
    };

    addCandidates(job);

    const lists = ['list_exe', 'list_plan', 'list_analise'];
    for (let i = 0; i < lists.length; i++) {
      const arr = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < arr.length; j++) addCandidates(arr[j]);
    }

    for (let k = 0; k < candidates.length; k++) {
      const lat = toNumber(candidates[k].lat, null);
      const lng = toNumber(candidates[k].lng, null);
      if (this.isValidLatLng(lat, lng)) return { lat: lat, lng: lng };
    }

    return null;
  };

  offsetPointForPlane = (point, index, total) => {
    if (!point || total <= 1) return point;

    const radiusDeg = 0.00008;
    const angle = (Math.PI * 2 * index) / total;
    return {
      lat: point.lat + Math.sin(angle) * radiusDeg,
      lng: point.lng + Math.cos(angle) * radiusDeg
    };
  };

  drawJobAreas = (job, areas) => {
    let allPoints = [];
    if (!areas || !areas.length) return allPoints;

    const planningAreas = Array.isArray(areas.planningAreas) && areas.planningAreas.length ? areas.planningAreas : areas;
    const executionAreas = Array.isArray(areas.executionAreas) ? areas.executionAreas : [];

    for (let i = 0; i < planningAreas.length; i++) {
      const area = planningAreas[i];
      const points = this.parseAreaPoints(area);
      if (!points.length) continue;
      allPoints = allPoints.concat(points);

      if (points.length < 3) {
        this.addPointLabel(points[0], area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id));
        continue;
      }

      const degrees = [];
      for (let j = 0; j < points.length; j++) {
        degrees.push(points[j].lng, points[j].lat, 2);
      }

      this.viewer.entities.add({
        name: area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id),
        polygon: {
          hierarchy: Cesium.Cartesian3.fromDegreesArrayHeights(degrees),
          material: Cesium.Color.fromCssColorString('#FF1493').withAlpha(0.38),
          outline: true,
          outlineColor: Cesium.Color.fromCssColorString('#47839e'),
          perPositionHeight: true
        }
      });
    }

    const reportPoints = this.drawJobApplicationReport(job, areas.applicationReport);
    if (reportPoints.length) {
      allPoints = allPoints.concat(reportPoints);
    } else if (executionAreas.length) {
      const applicationPoints = this.drawJobApplicationAreas(job, executionAreas);
      allPoints = allPoints.concat(applicationPoints);
    }

    const center = this.getCenterFromPoints(allPoints);
    if (center) this.addPointLabel(center, job.title || (this.props.res.TRABALHO + ' ' + job.id));
    return allPoints;
  };

  drawJobApplicationReport = (job, report) => {
    let allPoints = [];
    const applicationItems = this.getJobApplicationReportItems(report);

    if (!applicationItems.length) {
      return allPoints;
    }

    for (let i = 0; i < applicationItems.length; i++) {
      const item = applicationItems[i];
      if (!item) continue;

      const geometries = [];
      const reportGeometry = item.poly || item.polygon || item.geojson || item;

      this.collectApplicationGeometries(reportGeometry, geometries, 0);

      for (let j = 0; j < geometries.length; j++) {
        const geometry = geometries[j];
        if (!geometry || !Array.isArray(geometry.points) || geometry.points.length < 2) continue;

        const name = item.title || item.name || job.title || (this.props.res.TRABALHO + ' ' + job.id);

        if (geometry.type === 'line' && geometry.points.length >= 2) {
          const width = this.getApplicationWidth(item, job);
          const strip = this.buildApplicationStripFromLine(geometry.points, width);
          if (strip && strip.length >= 3 && !this.hasApplicationGeometryDrawn(strip)) {
            this.addApplicationPolygon(strip, name);
            allPoints = allPoints.concat(strip);
          }
        } else if (geometry.points.length >= 3 && !this.hasApplicationGeometryDrawn(geometry.points)) {
          this.addApplicationPolygon(geometry.points, name);
          allPoints = allPoints.concat(geometry.points);
        }
      }
    }

    return allPoints;
  };

  getJobApplicationReportItems = (report) => {
    if (!report) return [];

    if (report.apl && Array.isArray(report.apl.aplicacao)) return report.apl.aplicacao;
    if (Array.isArray(report.aplicacao)) return report.aplicacao;
    if (Array.isArray(report.application)) return report.application;
    if (Array.isArray(report.app)) return report.app;

    return [];
  };

  drawJobApplicationAreas = (job, areas) => {
    let allPoints = [];
    if (!Array.isArray(areas) || !areas.length) return allPoints;

    for (let i = 0; i < areas.length; i++) {
      const area = areas[i];
      const geometries = this.getApplicationGeometriesFromArea(area);

      for (let j = 0; j < geometries.length; j++) {
        const geometry = geometries[j];
        if (!geometry || !Array.isArray(geometry.points) || geometry.points.length < 2) continue;

        if (geometry.type === 'line' && geometry.points.length >= 2) {
          const width = this.getApplicationWidth(area, job);
          const strip = this.buildApplicationStripFromLine(geometry.points, width);
          if (strip && strip.length >= 3) {
            this.addApplicationPolygon(strip, area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id));
            allPoints = allPoints.concat(strip);
          }
        } else if (geometry.points.length >= 3) {
          this.addApplicationPolygon(geometry.points, area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id));
          allPoints = allPoints.concat(geometry.points);
        }
      }
    }

    return allPoints;
  };

  hasApplicationGeometryDrawn = (points) => {
    if (!Array.isArray(points) || !points.length) return true;

    const key = points.map((point) => {
      const lat = toNumber(point && point.lat, null);
      const lng = toNumber(point && point.lng, null);

      if (!this.isValidLatLng(lat, lng)) return '';
      return lat.toFixed(7) + ',' + lng.toFixed(7);
    }).filter(Boolean).join('|');

    if (!key) return true;

    if (!this.applicationGeometryKeys) {
      this.applicationGeometryKeys = {};
    }

    if (this.applicationGeometryKeys[key]) {
      return true;
    }

    this.applicationGeometryKeys[key] = true;
    return false;
  };

  addApplicationPolygon = (points, name) => {
    if (!this.viewer || !Array.isArray(points) || points.length < 3) return;

    const degrees = [];
    for (let i = 0; i < points.length; i++) {
      if (!this.isValidLatLng(points[i].lat, points[i].lng)) continue;
      degrees.push(points[i].lng, points[i].lat, 18);
    }

    if (degrees.length < 9) return;

    this.viewer.entities.add({
      name: name,
      polygon: {
        hierarchy: Cesium.Cartesian3.fromDegreesArrayHeights(degrees),
        material: Cesium.Color.fromCssColorString('#32a852').withAlpha(0.9),
        outline: true,
        outlineColor: Cesium.Color.fromCssColorString('#32a852'),
        perPositionHeight: true
      }
    });
  };

  getApplicationWidth = (area, job) => {
    const candidates = [
      area && area.faixa,
      area && area.width,
      area && area.largura,
      area && area.setLargura,
      job && job.faixa,
      job && job.width,
      job && job.largura
    ];

    for (let i = 0; i < candidates.length; i++) {
      const value = toNumber(candidates[i], null);
      if (value && value > 0) return value;
    }

    return 30;
  };

  getApplicationGeometriesFromArea = (area) => {
    const ret = [];
    if (!area) return ret;

    const fields = [
      area.application,
      area.applications,
      area.app,
      area.aplicacao,
      area.aplicacoes,
      area.spray,
      area.spray_points,
      area.sprayPoints,
      area.points_spray,
      area.pointsSpray,
      area.path,
      area.paths,
      area.track,
      area.tracks,
      area.geojson,
      area.output,
      area.result
    ];

    for (let i = 0; i < fields.length; i++) {
      this.collectApplicationGeometries(fields[i], ret, 0);
    }

    return ret;
  };

  collectApplicationGeometries = (value, ret, depth) => {
    if (depth > 5 || value === null || value === undefined) return;

    if (typeof value === 'string') {
      const text = value.trim();
      if (!text) return;
      try {
        this.collectApplicationGeometries(JSON.parse(text), ret, depth + 1);
      } catch (e) {}
      return;
    }

    if (Array.isArray(value)) {
      const points = this.parseLatLngList(value);
      if (points.length >= 2) {
        ret.push({ type: 'line', points: points });
        return;
      }

      for (let i = 0; i < value.length; i++) {
        this.collectApplicationGeometries(value[i], ret, depth + 1);
      }
      return;
    }

    if (typeof value !== 'object') return;

    if (value.type && String(value.type).toLowerCase() === 'featurecollection' && Array.isArray(value.features)) {
      for (let i = 0; i < value.features.length; i++) {
        this.collectApplicationGeometries(value.features[i], ret, depth + 1);
      }
    }

    if (value.type && value.geometry) {
      this.collectApplicationGeometries(value.geometry, ret, depth + 1);
    }

    if (value.type && value.coordinates) {
      this.collectGeoJsonApplication(value, ret, depth + 1);
    }

    if (value.left && value.right) {
      const left = this.parseLatLngList(value.left.points || value.left);
      const right = this.parseLatLngList(value.right.points || value.right);
      if (left.length >= 2 && right.length >= 2) {
        ret.push({ type: 'polygon', points: left.concat(right.slice().reverse()) });
      }
    }

    const keys = ['spray', 'spray_points', 'sprayPoints', 'points_spray', 'pointsSpray', 'application', 'applications', 'app', 'aplicacao', 'aplicacoes', 'poly', 'polygon', 'polygons', 'geojson', 'path', 'paths', 'track', 'tracks', 'line', 'lines', 'polyline', 'items', 'data', 'geometry'];
    for (let i = 0; i < keys.length; i++) {
      if (value[keys[i]] !== undefined) {
        this.collectApplicationGeometries(value[keys[i]], ret, depth + 1);
      }
    }
  };

  collectGeoJsonApplication = (geojson, ret, depth) => {
    if (!geojson || !geojson.type || !geojson.coordinates) return;

    const type = String(geojson.type).toLowerCase();
    const coordinates = geojson.coordinates;

    if (type === 'polygon') {
      for (let i = 0; i < coordinates.length; i++) {
        const points = this.parseLatLngList(coordinates[i]);
        if (points.length >= 3) ret.push({ type: 'polygon', points: points });
      }
    } else if (type === 'multipolygon') {
      for (let i = 0; i < coordinates.length; i++) {
        this.collectGeoJsonApplication({ type: 'Polygon', coordinates: coordinates[i] }, ret, depth + 1);
      }
    } else if (type === 'linestring') {
      const points = this.parseLatLngList(coordinates);
      if (points.length >= 2) ret.push({ type: 'line', points: points });
    } else if (type === 'multilinestring') {
      for (let i = 0; i < coordinates.length; i++) {
        const points = this.parseLatLngList(coordinates[i]);
        if (points.length >= 2) ret.push({ type: 'line', points: points });
      }
    }
  };

  parseLatLngList = (value) => {
    if (!Array.isArray(value)) return [];

    const points = [];
    for (let i = 0; i < value.length; i++) {
      const parsed = this.normalizeLatLngPair(value[i]);
      if (parsed) points.push(parsed);
    }

    return points;
  };

  buildApplicationStripFromLine = (line, widthMeters) => {
    if (!Array.isArray(line) || line.length < 2) return null;

    const safeWidth = Number.isFinite(widthMeters) && widthMeters > 0 ? widthMeters : 30;
    const projection = this.getLocalProjection(line[0]);
    const leftPoints = [];
    const rightPoints = [];
    const halfWidth = safeWidth / 2;

    for (let i = 0; i < line.length; i++) {
      const point = line[i];
      const previousPoint = line[i - 1] || point;
      const nextPoint = line[i + 1] || point;
      const previousXY = projection.toXY(previousPoint);
      const nextXY = projection.toXY(nextPoint);
      let dx = nextXY.x - previousXY.x;
      let dy = nextXY.y - previousXY.y;

      if (Math.abs(dx) < 0.0001 && Math.abs(dy) < 0.0001 && i > 0) {
        const currentXY = projection.toXY(point);
        const lastXY = projection.toXY(line[i - 1]);
        dx = currentXY.x - lastXY.x;
        dy = currentXY.y - lastXY.y;
      }

      const length = Math.sqrt((dx * dx) + (dy * dy));
      if (!Number.isFinite(length) || length <= 0) continue;

      const normal = { x: -(dy / length), y: dx / length };
      const xy = projection.toXY(point);
      const left = projection.toLatLng({ x: xy.x + (normal.x * halfWidth), y: xy.y + (normal.y * halfWidth) });
      const right = projection.toLatLng({ x: xy.x - (normal.x * halfWidth), y: xy.y - (normal.y * halfWidth) });

      leftPoints.push(left);
      rightPoints.push(right);
    }

    if (leftPoints.length < 2 || rightPoints.length < 2) return null;
    return leftPoints.concat(rightPoints.reverse());
  };

  getLocalProjection = (referencePoint) => {
    const earthRadius = 6378137;
    const refLatRad = Cesium.Math.toRadians(referencePoint.lat);
    const refLngRad = Cesium.Math.toRadians(referencePoint.lng);
    const cosLat = Math.cos(refLatRad) || 1;

    return {
      toXY: (point) => ({
        x: (Cesium.Math.toRadians(point.lng) - refLngRad) * earthRadius * cosLat,
        y: (Cesium.Math.toRadians(point.lat) - refLatRad) * earthRadius
      }),
      toLatLng: (xy) => ({
        lat: Cesium.Math.toDegrees((xy.y / earthRadius) + refLatRad),
        lng: Cesium.Math.toDegrees((xy.x / (earthRadius * cosLat)) + refLngRad)
      })
    };
  };

  addPointLabel = (point, text) => {
    this.viewer.entities.add({
      name: text,
      position: Cesium.Cartesian3.fromDegrees(point.lng, point.lat, 18),
      point: { pixelSize: 10, color: Cesium.Color.fromCssColorString('#1f8f47'), outlineColor: Cesium.Color.WHITE, outlineWidth: 2 },
      label: {
        text: text,
        font: '14px sans-serif',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -20)
      }
    });
  };

  parseAreaPoints = (area) => {
    if (!area) return [];

    if (area.radius > 0 && area.point) {
      try {
        const point = Array.isArray(area.point) ? area.point : JSON.parse(area.point);
        const parsed = this.normalizeLatLngPair(point);
        return parsed ? [parsed] : [];
      } catch (e) {}
    }

    if (!area.points) return [];

    try {
      const list = Array.isArray(area.points) ? area.points : JSON.parse(area.points);
      const points = [];
      for (let i = 0; i < list.length; i++) {
        const parsed = this.normalizeLatLngPair(list[i]);
        if (parsed) points.push(parsed);
      }
      return points;
    } catch (e) {
      return [];
    }
  };

  normalizeLatLngPair = (raw) => {
    if (!raw) return null;

    let lat = null;
    let lng = null;

    if (Array.isArray(raw) && raw.length >= 2) {
      const a = toNumber(raw[0], null);
      const b = toNumber(raw[1], null);
      if (this.isValidLatLng(a, b)) {
        lat = a;
        lng = b;
      } else if (this.isValidLatLng(b, a)) {
        lat = b;
        lng = a;
      }
    } else if (raw.lat !== undefined && raw.lng !== undefined) {
      lat = toNumber(raw.lat, null);
      lng = toNumber(raw.lng, null);
    } else if (raw.latitude !== undefined && raw.longitude !== undefined) {
      lat = toNumber(raw.latitude, null);
      lng = toNumber(raw.longitude, null);
    }

    if (!this.isValidLatLng(lat, lng)) return null;
    return { lat: lat, lng: lng };
  };

  drawPlane = (plane, point, rawPosition, job) => {
    const model = this.getAircraftModel(plane);
    const heading = this.getHeadingFromPosition(rawPosition) + model.modelHeadingOffsetDeg;
    const displayName = this.getPlaneDisplayName(plane) || model.label;
    const labelText = job ? this.getJobFocusLabel(job, [displayName]) : displayName;
    const position = Cesium.Cartesian3.fromDegrees(point.lng, point.lat, model.modelAltitudeOffsetMeters);
    const hpr = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(heading),
      Cesium.Math.toRadians(model.modelPitchOffsetDeg),
      Cesium.Math.toRadians(model.modelRollOffsetDeg)
    );

    const entity = this.viewer.entities.add({
      name: labelText,
      position: position,
      orientation: Cesium.Transforms.headingPitchRollQuaternion(position, hpr),
      model: {
        uri: model.url,
        scale: model.scale,
        minimumPixelSize: model.minimumPixelSize
      },
      label: {
        text: labelText,
        font: '14px sans-serif',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -34)
      }
    });

    entity.baseReplay3DModelScale = model.scale;
    this.aircraftEntities.push(entity);
    this.installAircraftScaleListener();
  };

  installAircraftScaleListener = () => {
    if (!this.viewer || !this.viewer.scene || this.aircraftScalePreRenderListener) return;

    this.aircraftScalePreRenderListener = () => {
      this.updateAircraftModelScales();
    };

    this.viewer.scene.preRender.addEventListener(this.aircraftScalePreRenderListener);
  };

  removeAircraftScaleListener = () => {
    if (this.viewer && this.viewer.scene && this.aircraftScalePreRenderListener) {
      this.viewer.scene.preRender.removeEventListener(this.aircraftScalePreRenderListener);
    }

    this.aircraftScalePreRenderListener = null;
  };

  updateAircraftModelScales = () => {
    if (!this.viewer || this.viewer.isDestroyed() || !this.viewer.camera || !Array.isArray(this.aircraftEntities)) return;

    const cameraPosition = this.viewer.camera.positionWC;
    const referenceDistance = this.aircraftScaleReferenceDistanceMeters;

    for (let i = 0; i < this.aircraftEntities.length; i++) {
      const entity = this.aircraftEntities[i];
      if (!entity || !entity.model || !entity.position) continue;

      const time = this.viewer.clock ? this.viewer.clock.currentTime : Cesium.JulianDate.now();
      const entityPosition = entity.position.getValue ? entity.position.getValue(time) : entity.position;
      if (!entityPosition) continue;

      const distance = Cesium.Cartesian3.distance(cameraPosition, entityPosition);
      if (!Number.isFinite(distance) || distance <= 0) continue;

      const baseScale = Number.isFinite(entity.baseReplay3DModelScale) && entity.baseReplay3DModelScale > 0 ? entity.baseReplay3DModelScale : 1;
      const scaleFactor = Math.max(
        this.aircraftScaleMinFactor,
        Math.min(this.aircraftScaleMaxFactor, distance / referenceDistance)
      );

      entity.model.scale = baseScale * scaleFactor;
    }
  };

  getHeadingFromPosition = (position) => {
    if (!position) return 0;
    return toNumber(position.angle !== undefined ? position.angle : position.heading, 0);
  };

  getAircraftModelById = (modelId) => {
    for (let i = 0; i < aircraftModels.length; i++) {
      if (aircraftModels[i].id === modelId) return aircraftModels[i];
    }

    return null;
  };

  normalizeReplay3DModelId = (value) => {
    const text = this.normalizeReplay3DText(value).replace(/_/g, '-');

    if (
      text === 'emb200' ||
      text === 'air-tractor-at-502b' ||
      text === 'air-tractor-at-802' ||
      text === 'cessna-188'
    ) {
      return text;
    }

    return '';
  };

  getAircraftModel = (plane) => {
    const model3D = this.normalizeReplay3DModelId(plane && plane.model3D);
    const modelByModel3D = this.getAircraftModelById(model3D);

    if (modelByModel3D) {
      return modelByModel3D;
    }

    const aircraftText = this.buildAircraftSearchText(plane);

    if (this.isCessnaReplay3DBrand(aircraftText)) {
      return aircraftModels.filter((item) => item.id === 'cessna-188')[0];
    }

    if (this.isAirTractor802Replay3DModel(aircraftText)) {
      return aircraftModels.filter((item) => item.id === 'air-tractor-at-802')[0];
    }

    if (this.isAirTractorReplay3DBrand(aircraftText) || this.isAirTractor502Replay3DModel(aircraftText)) {
      return aircraftModels.filter((item) => item.id === 'air-tractor-at-502b')[0];
    }

    if (this.isEmbraerReplay3DModel(aircraftText)) {
      return aircraftModels.filter((item) => item.id === 'emb200')[0];
    }

    return aircraftModels.filter((item) => item.id === 'emb200')[0];
  };

  buildAircraftSearchText = (plane) => {
    if (!plane) return '';

    const values = [];
    const add = (value) => {
      if (value === null || value === undefined) return;
      if (typeof value === 'object') {
        values.push(value.name);
        values.push(value.label);
        values.push(value.title);
        values.push(value.brand);
        values.push(value.model);
        values.push(value.aircraftBrand);
        values.push(value.aircraftModel);
        return;
      }
      values.push(value);
    };

    add(plane.name);
    add(plane.plane_name);
    add(plane.job_plane_name);
    add(plane.plane);
    add(plane.prefix);
    add(plane.model);
    add(plane.model_name);
    add(plane.aircraftmodel);
    add(plane.aircraftModel);
    add(plane.aircraft_model);
    add(plane.aircraft_model_name);
    add(plane.plane_model);
    add(plane.brand);
    add(plane.brand_name);
    add(plane.aircraftbrand);
    add(plane.aircraftBrand);
    add(plane.aircraft_brand);
    add(plane.aircraft_brand_name);
    add(plane.manufacturer);
    add(plane.manufacturer_name);
    add(plane.aircraft);
    add(plane.aircraft_type);

    return this.normalizeReplay3DText(values.join(' '));
  };

  normalizeReplay3DText = (value) => {
    let text = String(value || '').trim().toLowerCase();
    if (text.normalize) text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return text;
  };

  compactReplay3DText = (value) => {
    return this.normalizeReplay3DText(value).replace(/[\s_-]+/g, '');
  };

  isAirTractorReplay3DBrand = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('airtractor') >= 0 || compactText.indexOf('airtractorat502') >= 0;
  };

  isAirTractor502Replay3DModel = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('at502') >= 0 || compactText.indexOf('at502b') >= 0 || compactText.indexOf('502b') >= 0;
  };

  isAirTractor802Replay3DModel = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('at802') >= 0 || compactText.indexOf('at802a') >= 0 || compactText.indexOf('airtractorat802') >= 0 || compactText.indexOf('802a') >= 0;
  };

  isCessnaReplay3DBrand = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('cessna') >= 0 || compactText.indexOf('cessna188') >= 0;
  };

  isEmbraerReplay3DModel = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('embraer') >= 0 || compactText.indexOf('emb200') >= 0 || compactText.indexOf('ipanema') >= 0;
  };

  getValidPosition = (position) => {
    if (!position) return null;
    const candidates = [
      { lat: position.lat, lng: position.lng },
      { lat: position.latitude, lng: position.longitude },
      { lat: position.y, lng: position.x }
    ];

    for (let i = 0; i < candidates.length; i++) {
      const lat = toNumber(candidates[i].lat, null);
      const lng = toNumber(candidates[i].lng, null);
      if (this.isValidLatLng(lat, lng)) return { lat: lat, lng: lng };
    }
    return null;
  };

  getPlaneFallbackPosition = (plane, jobs, areasByJob) => {
    for (let i = 0; i < jobs.length; i++) {
      const listExe = Array.isArray(jobs[i].list_exe) ? jobs[i].list_exe : [];
      for (let j = 0; j < listExe.length; j++) {
        if (toNumber(listExe[j].id_plane, 0) === toNumber(plane.id, 0)) {
          const areas = areasByJob[jobs[i].id] || [];
          let points = [];
          for (let k = 0; k < areas.length; k++) points = points.concat(this.parseAreaPoints(areas[k]));
          const center = this.getCenterFromPoints(points);
          if (center) return center;
        }
      }
    }
    return this.getDefaultPlace();
  };

  getDefaultPlace = () => {
    if (Config.place && Config.place.length >= 2) {
      const lat = toNumber(Config.place[0], null);
      const lng = toNumber(Config.place[1], null);
      if (this.isValidLatLng(lat, lng)) return { lat: lat, lng: lng };
    }
    return { lat: -30.0346, lng: -51.2177 };
  };

  getCenterFromPoints = (points) => {
    if (!points || !points.length) return null;
    let lat = 0;
    let lng = 0;
    let qtd = 0;
    for (let i = 0; i < points.length; i++) {
      if (this.isValidLatLng(points[i].lat, points[i].lng)) {
        lat += points[i].lat;
        lng += points[i].lng;
        qtd++;
      }
    }
    return qtd ? { lat: lat / qtd, lng: lng / qtd } : null;
  };

  isValidLatLng = (lat, lng) => {
    return lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng) && lat <= 90 && lat >= -90 && lng <= 180 && lng >= -180 && !(lat === 0 && lng === 0);
  };

  flyToSelectedJob = () => {
    const focusItems = this.state.focusItems && this.state.focusItems.length ? this.state.focusItems : this.focusItems;
    const safeIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    if (focusItems[safeIndex]) {
      this.flyToJobFocus(focusItems[safeIndex]);
    } else {
      this.flyToPoints(this.getCurrentPoints());
    }
  };

  flyToJobFocus = (focusItem) => {
    if (!focusItem || !focusItem.point || !this.viewer) return;

    const point = focusItem.point;
    const bounds = focusItem.bounds && focusItem.bounds.length ? focusItem.bounds : [point];
    const rangeMeters = this.getFocusCameraRangeMeters(bounds);
    const target = Cesium.Cartesian3.fromDegrees(point.lng, point.lat, 0);
    const heading = this.viewer.camera && this.viewer.camera.heading ? this.viewer.camera.heading : Cesium.Math.toRadians(0);

    this.viewer.camera.flyToBoundingSphere(new Cesium.BoundingSphere(target, 120), {
      offset: new Cesium.HeadingPitchRange(
        heading,
        Cesium.Math.toRadians(-54),
        rangeMeters
      ),
      duration: 0.7
    });
  };

  getFocusCameraRangeMeters = (points) => {
    const valid = (points || []).filter((p) => this.isValidLatLng(p.lat, p.lng));
    if (!valid.length) return 850;

    let maxDistance = 0;
    for (let i = 0; i < valid.length; i++) {
      for (let j = i + 1; j < valid.length; j++) {
        maxDistance = Math.max(maxDistance, this.distanceMeters(valid[i], valid[j]));
      }
    }

    if (!maxDistance || maxDistance < 250) return 760;
    return Math.max(820, Math.min(1800, maxDistance * 1.25));
  };

  distanceMeters = (a, b) => {
    if (!a || !b) return 0;
    const earthRadius = 6371000;
    const lat1 = Cesium.Math.toRadians(a.lat);
    const lat2 = Cesium.Math.toRadians(b.lat);
    const dLat = Cesium.Math.toRadians(b.lat - a.lat);
    const dLng = Cesium.Math.toRadians(b.lng - a.lng);
    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
    return earthRadius * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  };

  goToNextJob = () => {
    const focusItems = this.state.focusItems || [];
    if (!focusItems.length) return;
    const nextIndex = (this.state.currentJobIndex + 1) >= focusItems.length ? 0 : this.state.currentJobIndex + 1;
    this.setState({ currentJobIndex: nextIndex, freeMode: false }, () => this.flyToSelectedJob());
  };

  goToPreviousJob = () => {
    const focusItems = this.state.focusItems || [];
    if (!focusItems.length) return;
    const previousIndex = (this.state.currentJobIndex - 1) < 0 ? focusItems.length - 1 : this.state.currentJobIndex - 1;
    this.setState({ currentJobIndex: previousIndex, freeMode: false }, () => this.flyToSelectedJob());
  };

  flyToSelectedPlane = () => {
    const planeFocusItems = this.state.planeFocusItems || [];
    const safeIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, planeFocusItems.length);
    if (planeFocusItems[safeIndex]) {
      this.flyToJobFocus(planeFocusItems[safeIndex]);
    } else {
      this.flyToPoints(this.getCurrentPoints());
    }
  };

  goToNextPlane = () => {
    const planeFocusItems = this.state.planeFocusItems || [];
    if (!planeFocusItems.length) return;
    const nextIndex = (this.state.currentPlaneIndex + 1) >= planeFocusItems.length ? 0 : this.state.currentPlaneIndex + 1;
    this.setState({ currentPlaneIndex: nextIndex, freeMode: false }, () => this.flyToSelectedPlane());
  };

  goToPreviousPlane = () => {
    const planeFocusItems = this.state.planeFocusItems || [];
    if (!planeFocusItems.length) return;
    const previousIndex = (this.state.currentPlaneIndex - 1) < 0 ? planeFocusItems.length - 1 : this.state.currentPlaneIndex - 1;
    this.setState({ currentPlaneIndex: previousIndex, freeMode: false }, () => this.flyToSelectedPlane());
  };

  toggleFreeMode = () => {
    const nextFreeMode = !this.state.freeMode;
    this.setState({ freeMode: nextFreeMode }, () => {
      if (!nextFreeMode) this.flyToSelectedJob();
    });
  };

  centerMap = () => {
    if (this.state.freeMode) {
      this.flyToPoints(this.getCurrentPoints());
      return;
    }
    this.flyToSelectedJob();
  };

  flyToPoints = (points, closer) => {
    if (!this.viewer) return;
    const valid = (points || []).filter((p) => this.isValidLatLng(p.lat, p.lng));
    if (!valid.length) {
      this.flyToDefaultPlace();
      return;
    }

    let minLat = valid[0].lat;
    let maxLat = valid[0].lat;
    let minLng = valid[0].lng;
    let maxLng = valid[0].lng;

    for (let i = 1; i < valid.length; i++) {
      minLat = Math.min(minLat, valid[i].lat);
      maxLat = Math.max(maxLat, valid[i].lat);
      minLng = Math.min(minLng, valid[i].lng);
      maxLng = Math.max(maxLng, valid[i].lng);
    }

    const minDelta = closer ? 0.0018 : 0.004;
    const deltaLat = Math.max(maxLat - minLat, minDelta);
    const deltaLng = Math.max(maxLng - minLng, minDelta);
    const padLat = deltaLat * (closer ? 0.25 : 0.35);
    const padLng = deltaLng * (closer ? 0.25 : 0.35);

    const rectangle = Cesium.Rectangle.fromDegrees(minLng - padLng, minLat - padLat, maxLng + padLng, maxLat + padLat);
    this.viewer.camera.flyTo({
      destination: rectangle,
      orientation: { heading: Cesium.Math.toRadians(0), pitch: Cesium.Math.toRadians(-52), roll: 0 },
      duration: 0.8
    });
  };

  flyToDefaultPlace = () => {
    if (!this.viewer) return;
    const place = this.getDefaultPlace();
    this.viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(place.lng, place.lat, 2500),
      orientation: { heading: Cesium.Math.toRadians(0), pitch: Cesium.Math.toRadians(-55), roll: 0 }
    });
    this.viewer.scene.requestRender();
  };

  zoomInMap = () => {
    if (!this.viewer) return;
    this.viewer.camera.zoomIn(this.viewer.camera.positionCartographic.height * 0.35);
  };

  zoomOutMap = () => {
    if (!this.viewer) return;
    this.viewer.camera.zoomOut(this.viewer.camera.positionCartographic.height * 0.35);
  };

  rotateLeft = () => {
    if (!this.viewer) return;
    this.viewer.camera.rotate(Cesium.Cartesian3.UNIT_Z, Cesium.Math.toRadians(12));
  };

  rotateRight = () => {
    if (!this.viewer) return;
    this.viewer.camera.rotate(Cesium.Cartesian3.UNIT_Z, Cesium.Math.toRadians(-12));
  };

  tiltUp = () => {
    if (!this.viewer) return;
    this.viewer.camera.lookUp(Cesium.Math.toRadians(8));
  };

  tiltDown = () => {
    if (!this.viewer) return;
    this.viewer.camera.lookDown(Cesium.Math.toRadians(8));
  };

  getSelectedFocusItem = () => {
    const focusItems = this.state.focusItems || [];
    if (!focusItems.length) return null;
    const index = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    return focusItems[index] || null;
  };

  getSelectedFocusTitle = () => {
    const focusItem = this.getSelectedFocusItem();
    if (!focusItem) return this.props.res.NENHUM_TRABALHO_FOCO;
    const total = this.state.focusItems.length;
    const numberText = total > 1 ? ((this.state.currentJobIndex + 1) + '/' + total + ' - ') : '';
    return numberText + this.getJobDisplayName(focusItem.job);
  };

  getSelectedFocusSubtitle = () => {
    const focusItem = this.getSelectedFocusItem();
    if (!focusItem) return this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;
    if (this.state.freeMode) return this.props.res.MODO_LIVRE_ATIVO;
    const planeText = focusItem.planeNames && focusItem.planeNames.length ? focusItem.planeNames.join(', ') : this.props.res.AERONAVE_NAO_INFORMADA;
    return this.props.res.AERONAVE + ': ' + planeText;
  };

  getSelectedPlaneFocusItem = () => {
    const planeFocusItems = this.state.planeFocusItems || [];
    if (!planeFocusItems.length) return null;
    const index = this.getSafeJobIndex(this.state.currentPlaneIndex, planeFocusItems.length);
    return planeFocusItems[index] || null;
  };

  getSelectedPlaneFocusTitle = () => {
    const focusItem = this.getSelectedPlaneFocusItem();
    if (!focusItem) return this.props.res.AERONAVE_NAO_INFORMADA;
    const total = this.state.planeFocusItems.length;
    const numberText = total > 1 ? ((this.state.currentPlaneIndex + 1) + '/' + total + ' - ') : '';
    return numberText + this.getPlaneDisplayName(focusItem.plane);
  };

  getSelectedPlaneFocusSubtitle = () => {
    const focusItem = this.getSelectedPlaneFocusItem();
    if (!focusItem) return this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;
    if (this.state.freeMode) return this.props.res.MODO_LIVRE_ATIVO;

    const jobs = focusItem.jobs || [];
    if (!jobs.length) return this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;

    const jobNames = jobs.map((job) => this.getJobDisplayName(job)).filter(Boolean);
    return this.props.res.TRABALHOS + ': ' + jobNames.join(', ');
  };

  renderPanel = () => {
    const res = this.props.res || {};
    const jobs = this.state.jobs || [];
    const planes = this.state.planes || [];
    const canNavigateJobs = (this.state.focusItems || []).length > 1;
    const canNavigatePlanes = (this.state.planeFocusItems || []).length > 1;

    return (
      <aside className="panel">
        <div className="panel-header">
          <h1>{res.MAPA_FAZENDA_ONLINE}</h1>
          <button id="panelToggleBtn" className="panel-toggle" type="button" aria-pressed="false" title={res.OCULTAR_MENU_LATERAL} onClick={this.togglePanelVisibility}>{res.OCULTAR_MENU}</button>
        </div>
        <p className="subtitle">{res.MAPA_3D_TRABALHOS_EXECUCAO_CICLO}</p>

        <div className="field">
          <label>{res.NAVEGACAO_MAPA}</label>
          <div className="controls map-controls">
            <button type="button" onClick={this.zoomInMap}>{res.ZOOM_MAIS}</button>
            <button type="button" onClick={this.zoomOutMap}>{res.ZOOM_MENOS}</button>
            <button type="button" onClick={this.centerMap}>{res.CENTRALIZAR}</button>
          </div>
        </div>

        <div className="field">
          <label>{res.TRABALHO_EM_FOCO}</label>
          <div className="status" style={{ marginTop: 0 }}>
            <div><strong>{this.getSelectedFocusTitle()}</strong></div>
            <div style={{ marginTop: 4 }}>{this.getSelectedFocusSubtitle()}</div>
          </div>
          <div className="controls map-controls">
            <button type="button" onClick={this.goToPreviousJob} disabled={!canNavigateJobs}>{res.ANTERIOR}</button>
            <button type="button" onClick={this.flyToSelectedJob} disabled={!jobs.length}>{res.IR_PARA}</button>
            <button type="button" onClick={this.goToNextJob} disabled={!canNavigateJobs}>{res.PROXIMO}</button>
          </div>
          <div className="controls one-col">
            <button type="button" onClick={this.toggleFreeMode}>{this.state.freeMode ? res.FOCAR_TRABALHO_SELECIONADO : res.MODO_LIVRE}</button>
          </div>
        </div>

        <div className="field">
          <label>{res.AERONAVE}</label>
          <div className="status" style={{ marginTop: 0 }}>
            <div><strong>{this.getSelectedPlaneFocusTitle()}</strong></div>
            <div style={{ marginTop: 4 }}>{this.getSelectedPlaneFocusSubtitle()}</div>
          </div>
          <div className="controls map-controls">
            <button type="button" onClick={this.goToPreviousPlane} disabled={!canNavigatePlanes}>{res.ANTERIOR}</button>
            <button type="button" onClick={this.flyToSelectedPlane} disabled={!(this.state.planeFocusItems || []).length}>{res.IR_PARA}</button>
            <button type="button" onClick={this.goToNextPlane} disabled={!canNavigatePlanes}>{res.PROXIMO}</button>
          </div>
        </div>

        <div className="field">
          <label>{res.GIRO}</label>
          <div className="controls camera-btns">
            <button type="button" onClick={this.rotateLeft}>{res.ESQUERDA}</button>
            <button type="button" onClick={this.rotateRight}>{res.DIREITA}</button>
          </div>
        </div>

        <div className="field">
          <label>{res.INCLINACAO}</label>
          <div className="controls camera-btns">
            <button type="button" onClick={this.tiltUp}>{res.SUBIR}</button>
            <button type="button" onClick={this.tiltDown}>{res.DEITAR}</button>
          </div>
        </div>

        <div className="status">
          <div><strong>{res.TRABALHOS}:</strong> {jobs.length}</div>
          <div><strong>{res.AERONAVE}:</strong> {planes.length}</div>
          {this.state.message ? <div style={{ marginTop: 8 }}>{this.state.message}</div> : null}
        </div>
      </aside>
    );
  };

  getCurrentPoints = () => {
    const entities = this.viewer && this.viewer.entities ? this.viewer.entities.values : [];
    const points = [];
    for (let i = 0; i < entities.length; i++) {
      const position = entities[i].position && entities[i].position.getValue ? entities[i].position.getValue(Cesium.JulianDate.now()) : null;
      if (!position) continue;
      const cartographic = Cesium.Cartographic.fromCartesian(position);
      points.push({ lat: Cesium.Math.toDegrees(cartographic.latitude), lng: Cesium.Math.toDegrees(cartographic.longitude) });
    }
    return points;
  };

  renderLoading = () => {
    const res = this.props.res || {};
    if (!this.state.loading) return null;
    return (
      <div className="replay3d-base-loading">
        <div className="replay3d-base-loading-card">
          <div className="replay3d-base-loading-spinner" />
          <div className="replay3d-base-loading-title">{res.CARREGANDO_MAPA_3D}</div>
          <div className="replay3d-base-loading-text">{res.PREPARANDO_MAPA_3D_TRABALHOS}</div>
        </div>
      </div>
    );
  };

  render() {
    const res = this.props.res || {};
    const fullscreen = this.state.fullscreen;
    const fullscreenIcon = fullscreen ? 'fullscreen_exit' : 'fullscreen';
    const fullscreenTitle = fullscreen ? res.SAIR_TELA_CHEIA : res.TELA_CHEIA;

    return (
      <div className="replay3d-base-overlay">
        <div className="replay3d-base-app-host">
          <div id="replay3dBaseApp" className={this.state.panelCollapsed ? 'panel-collapsed' : ''}>
            {this.renderPanel()}
            <button id="panelRestoreBtn" className="panel-restore-btn" type="button" aria-pressed="true" title={res.MOSTRAR_MENU_LATERAL} onClick={this.togglePanelVisibility}>{res.MOSTRAR_MENU}</button>
            <div id="cesiumContainer">
              <div id={this.containerId} style={{ width: '100%', height: '100%' }} />
            </div>
          </div>
        </div>

        {this.renderLoading()}

        <div className="replay3d-base-action-buttons">
          <Button className="replay3d-base-action-button replay3d-base-fullscreen-button" onClick={this.toggleFullscreen} title={fullscreenTitle}>
            <MaterialIcon icon={fullscreenIcon} className="iconMap" />
          </Button>
          <Button className="replay3d-base-action-button replay3d-base-close-button" onClick={this.props.onClose} title={res.FECHAR_MAPA_3D}>
            <MaterialIcon icon="close" className="iconMap" />
          </Button>
        </div>
      </div>
    );
  }
}

export default Map3D;